import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Drawer, Empty, Spin } from 'antd'
import { listActivity } from '@/api/activity'
import BrandButton from '@/components/BrandButton'
import ActivityClose from './images/activity-close.svg?react'
import { ActivityRow } from './ActivityRow'
import { activityDestination } from './activityTarget'
import { toActivityItem } from './mapActivity'
import type { ActivityItem } from './types'

const PAGE_SIZE = 20

interface RecentActivityDrawerProps {
  open: boolean
  /** 当前空间就绪后才请求；切换空间时重拉 */
  spaceReady: boolean
  spaceId: string | null
  onClose: () => void
  onOpenItem: (item: ActivityItem) => void
}

function RecentActivityDrawer({
  open,
  spaceReady,
  spaceId,
  onClose,
  onOpenItem,
}: RecentActivityDrawerProps) {
  const { t, i18n } = useTranslation()
  const pageLanguage = i18n.resolvedLanguage ?? i18n.language
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<ActivityItem[]>([])
  const [itemsSpaceId, setItemsSpaceId] = useState<string | null>(null)
  const [itemsLanguage, setItemsLanguage] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const itemsCurrent = itemsSpaceId === spaceId && itemsLanguage === pageLanguage
  const itemsForSpace = itemsCurrent ? items : []
  const listStale = spaceReady && !itemsCurrent

  useEffect(() => {
    setPage(1)
  }, [spaceId])

  useEffect(() => {
    if (!open || !spaceReady) return
    let cancelled = false
    setLoading(true)
    setError(false)
    void listActivity({ page, page_size: PAGE_SIZE })
      .then((result) => {
        if (cancelled) return
        setItems(result.list.map(toActivityItem))
        setItemsSpaceId(spaceId)
        setItemsLanguage(pageLanguage)
        setTotal(result.total)
      })
      .catch(() => {
        if (cancelled) return
        setItems([])
        setItemsSpaceId(spaceId)
        setItemsLanguage(pageLanguage)
        setTotal(0)
        setError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, page, pageLanguage, spaceId, spaceReady])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={614}
      closable={false}
      title={
        <span className="text-lg leading-[25px] font-medium text-black">
          {t('workbench.activityDrawerTitle')}
        </span>
      }
      extra={
        <div className="flex items-center gap-5">
          <span aria-hidden className="h-[30px] w-px bg-[rgba(0,0,0,0.04)]" />
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-3.5 w-3.5 cursor-pointer items-center justify-center border-0 bg-transparent p-0"
          >
            <ActivityClose className="h-3.5 w-3.5" />
          </button>
        </div>
      }
      classNames={{
        header: '!bg-transparent',
        body: '!bg-transparent [scrollbar-width:none] [&::-webkit-scrollbar]:w-0',
        section: '!overflow-hidden',
      }}
      styles={{
        body: {
          padding: '10px 20px 20px',
          background: 'transparent',
        },
        header: {
          padding: 20,
          borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
          background: 'transparent',
        },
        title: {
          fontWeight: 500,
          fontSize: 18,
          lineHeight: '25px',
          color: '#000',
        },
        section: {
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: 'inset 0 0 0 1px #DCDCDC',
        },
      }}
      destroyOnHidden
      afterOpenChange={(visible) => {
        if (!visible) setPage(1)
      }}
    >
      {error && !listStale ? (
        <Empty description={t('workbench.activityLoadFailed')} />
      ) : !spaceReady ? (
        <Empty description={t('workbench.activityEmpty')} />
      ) : listStale || (loading && itemsForSpace.length === 0) ? (
        <div className="flex justify-center py-16">
          <Spin />
        </div>
      ) : itemsForSpace.length === 0 ? (
        <Empty description={t('workbench.activityEmpty')} />
      ) : (
        <div className="flex flex-col gap-2.5">
          {itemsForSpace.map((item) => (
            <ActivityRow
              key={item.id}
              item={item}
              compact={false}
              onClick={
                activityDestination(item)
                  ? () => onOpenItem(item)
                  : undefined
              }
            />
          ))}
        </div>
      )}
      {itemsSpaceId === spaceId && total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-end gap-2">
          <BrandButton
            variant="outline"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t('workbench.paginationPrev')}
          </BrandButton>
          <span className="px-1 text-[13px] leading-5 text-black/60">
            {t('workbench.paginationPage', { page })}
          </span>
          <BrandButton
            variant="outline"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            {t('workbench.paginationNext')}
          </BrandButton>
        </div>
      )}
    </Drawer>
  )
}

export default RecentActivityDrawer
